import React, { useState, useEffect } from 'react';
import { useSettings } from '../hooks/useSettings';
import { Spinner } from './Spinner';
import { ExportSettingsManager } from './ExportSettingsManager';
import { UserManager } from './UserManager';
import { appSettingsSections, AppSettingsViewId } from '../config/adminConfig';
import { ApiSettings } from './ApiSettings';
import { LoggingSettings } from './LoggingSettings';
import { ScannerSettings } from './ScannerSettings';
import { PromptManager } from './PromptManager';
import { TouchDragTest } from './TouchDragTest';

export const AppSettings: React.FC = () => {
    const [activeView, setActiveView] = useState<AppSettingsViewId>(appSettingsSections[0].id as AppSettingsViewId);
    const [isInitialLoading, setIsInitialLoading] = useState(true);
    const { isLoading, refresh } = useSettings();

    useEffect(() => {
        if (isLoading === false) {
            setIsInitialLoading(false);
        }
    }, [isLoading]);

    useEffect(() => {
        refresh();
    }, [activeView, refresh]);

    const activeConfig = appSettingsSections.find(s => s.id === activeView);

    const NavButton: React.FC<{ view: AppSettingsViewId; label: string; icon: React.ReactNode }> = ({ view, label, icon }) => (
        <button
            onClick={() => setActiveView(view)}
            className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md transition-colors ${activeView === view ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-200'}`}
        >
            {icon}
            {label}
        </button>
    );

    const renderActiveView = () => {
        if (!activeConfig) return null;
        
        switch (activeConfig.id) {
            case 'api':
                return <ApiSettings title={activeConfig.managerTitle} />;
            case 'export':
                return <ExportSettingsManager title={activeConfig.managerTitle} />;
            case 'users':
                return <UserManager title={activeConfig.managerTitle} />;
            case 'prompts':
                return <PromptManager title={activeConfig.managerTitle} />;
            case 'scanner':
                return <ScannerSettings title={activeConfig.managerTitle} />;
            case 'logging':
                return <LoggingSettings />;
            case 'touchTest':
                return <TouchDragTest />;
            default:
                return null;
        }
    };

    if (isInitialLoading) {
        return <Spinner text="Načítám nastavení..." />;
    }

    return (
        <div className="space-y-6">
            <div className="flex items-center gap-2 border-b pb-4 flex-wrap">
                {appSettingsSections.map(section => (
                    <NavButton
                        key={section.id}
                        view={section.id as AppSettingsViewId}
                        label={section.navLabel}
                        icon={React.createElement(section.icon, { className: 'w-5 h-5' })}
                    />
                ))}
            </div>
            {renderActiveView()}
        </div>
    );
};
